import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useOrganization } from "@/hooks/useOrganization";

// ============================================================================
// Types
// ============================================================================

export interface AssignmentConnection {
  id: string;
  org_id: string;
  source_id: string;
  target_id: string;
  created_at: string;
}

// ============================================================================
// Query key
// ============================================================================

export const assignmentConnectionsKey = (orgId: string | undefined) =>
  ["assignment_connections", orgId] as const;

// ============================================================================
// Hook: fetch all connections for the org
//
// We fetch all connections for the org rather than filtering by date range —
// the set is small (one row per drawn line) and filtering by visible assignments
// is done in computeTransferConnections() in memory.
// ============================================================================

export function useAssignmentConnections() {
  const { data: org } = useOrganization();

  return useQuery({
    queryKey: assignmentConnectionsKey(org?.id),
    queryFn: async () => {
      if (!org?.id) return [] as AssignmentConnection[];
      const { data, error } = await supabase
        .from("assignment_connection")
        .select("id, org_id, source_id, target_id, created_at")
        .eq("org_id", org.id)
        .order("created_at");
      if (error) throw error;
      return data as AssignmentConnection[];
    },
    enabled: !!org?.id,
    staleTime: 30_000,
  });
}

// ============================================================================
// Mutation: create a connection
// ============================================================================

export function useCreateConnection() {
  const queryClient = useQueryClient();
  const { data: org } = useOrganization();

  return useMutation({
    mutationFn: async ({ sourceId, targetId }: { sourceId: string; targetId: string }) => {
      if (!org?.id) throw new Error("No org");
      const { data, error } = await supabase
        .from("assignment_connection")
        .insert({ org_id: org.id, source_id: sourceId, target_id: targetId })
        .select("id, org_id, source_id, target_id, created_at")
        .single();
      if (error) {
        // Unique constraint violation = connection already exists — treat as success
        if (error.code === "23505") return null;
        throw error;
      }
      return data as AssignmentConnection;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: assignmentConnectionsKey(org?.id) });
    },
  });
}

// ============================================================================
// Mutation: delete a connection by ID
// ============================================================================

export function useDeleteConnection() {
  const queryClient = useQueryClient();
  const { data: org } = useOrganization();

  return useMutation({
    mutationFn: async (connectionId: string) => {
      const { error } = await supabase
        .from("assignment_connection")
        .delete()
        .eq("id", connectionId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: assignmentConnectionsKey(org?.id) });
    },
  });
}
